

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import LoginStepOne from "./Auth/LoginStepOne";
import OtpLogin from "./Auth/OtpLogin";
import SignupForm from "./Auth/SignupForm";
import PasswordLogin from "./Auth/PasswordLogin";
import ForgotPasswordForm from "./Auth/ForgotPasswordForm";
import ResetPasswordForm from "./Auth/ResetPasswordForm";

export const AuthModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [authMode, setAuthMode] = useState("login"); // login | signup
  const [loginStep, setLoginStep] = useState("identifier"); // identifier | otp | password
  const [identifier, setIdentifier] = useState("");
  const [forgotStep, setForgotStep] = useState(null); // null | "email" | "reset"
  const [resetEmail, setResetEmail] = useState("");

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setAuthMode("login");
        setLoginStep("identifier");
        setIdentifier("");
        setForgotStep(null);
        setResetEmail("");
      }, 300);
    }
  }, [isOpen]);

  const handleBackToIdentifier = () => {
    setLoginStep("identifier");
    setIdentifier("");
  };

  const handleSwitchMode = () => {
    setAuthMode(authMode === "login" ? "signup" : "login");
    setLoginStep("identifier");
    setIdentifier("");
  };

  const handleSuccess = () => {
    onClose();
    if (onLoginSuccess) onLoginSuccess();
  };

  const isImageAuthLayout =
    (authMode === "login" && loginStep === "identifier") ||
    authMode === "signup" ||
    forgotStep === "email" ||
    forgotStep === "reset";
  const isLoginImageLayout =
    authMode === "login" && loginStep === "identifier" && !forgotStep;
  const imageAuthMaxWidth = isLoginImageLayout ? "900px" : "900px";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            style={{ 
              position: "fixed", 
              inset: 0, 
              background: "rgba(0,0,0,0.6)", 
              backdropFilter: "blur(4px)", 
              zIndex: 50 
            }}
          />

          {/* Modal Wrapper */}
          <div style={{ 
            position: "fixed", 
            inset: 0, 
            zIndex: 51, 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            padding: "20px", 
            pointerEvents: "none", 
            overflowY: "auto" 
          }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              style={{ 
                width: "100%", 
                maxWidth: isImageAuthLayout
                  ? imageAuthMaxWidth
                  : "420px",
                maxHeight: "90vh",
                pointerEvents: "auto" 
              }}
            >
              {/* Card */}
              <div style={{
                position: "relative", 
                background: isImageAuthLayout ? "transparent" : "#111315", 
                borderRadius: isImageAuthLayout ? "0" : "24px", 
                boxShadow: isImageAuthLayout ? "none" : "0 25px 60px rgba(0,0,0,0.25)", 
                border: isImageAuthLayout ? "none" : "1px solid #2f343b",
                overflow: "hidden",
                height: isImageAuthLayout ? "auto" : "500px"
              }}>
                {/* Close Button */}
                <button 
                  onClick={onClose} 
                  style={{ 
                    position: "absolute", 
                    top: isImageAuthLayout ? "30px" : "16px", 
                    right: isImageAuthLayout ? "12px" : "16px", 
                    border: "none", 
                    background: isImageAuthLayout ? "rgba(15, 23, 42, 0.55)" : "transparent",
                    width: isImageAuthLayout ? "32px" : "auto",
                    height: isImageAuthLayout ? "32px" : "auto",
                    borderRadius: isImageAuthLayout ? "999px" : "0",
                    color: isImageAuthLayout ? "#ffffff" : "#111827",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10
                  }}
                >
                  <X size={20} />
                </button>

                <div style={{ 
                  padding: isImageAuthLayout ? "0" : "32px", 
                  height: "100%",
                  overflow: isImageAuthLayout ? "visible" : "hidden",
                  position: "relative"
                }}>
                  {!forgotStep && !isImageAuthLayout && (
                    <div style={{ textAlign: "center", marginBottom: "24px" }}>
                      <h2 style={{ 
                        fontSize: "26px", 
                        marginBottom: "8px", 
                        color: '#f3f4f6',
                        fontWeight: 700
                      }}>
                        {authMode === "login" ? "Welcome Back" : "Create Account"}
                      </h2>
                      <span style={{ color: "#9ca3af", fontSize: "14px" }}>
                        {authMode === "login"
                          ? "Sign in to BlackRoth TM account"
                          : "Join BlackRoth TM today"}
                      </span>
                    </div>
                  )}

                  <AnimatePresence mode="wait">
                    {/* Forgot Password Flow */}
                    {forgotStep === "email" ? (
                      <motion.div
                        key="forgot-email"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ForgotPasswordForm
                          onSuccess={(email) => {
                            setResetEmail(email);
                            setForgotStep("reset");
                          }}
                          onBack={() => setForgotStep(null)}
                        />
                      </motion.div>
                    ) : forgotStep === "reset" ? (
                      <motion.div
                        key="forgot-reset"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ResetPasswordForm
                          email={resetEmail}
                          onBack={() => setForgotStep("email")}
                          onSuccess={() => {
                            setForgotStep(null);
                            setLoginStep("identifier");
                          }}
                        />
                      </motion.div>

                    ) : authMode === "signup" ? (
                      <motion.div
                        key="signup"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <SignupForm
                          onSuccess={handleSuccess}
                          onSwitchToLogin={handleSwitchMode}
                        />
                      </motion.div>

                    ) : loginStep === "identifier" ? (
                      /* Updated LoginStepOne with exact HTML design */
                      <motion.div
                        key="login-identifier"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <LoginStepOne
                          onSuccess={handleSuccess}
                          onSwitchToSignup={handleSwitchMode}
                          onForgotPassword={() => setForgotStep("email")}
                        />
                      </motion.div>

                    ) : loginStep === "password" ? (
                      <motion.div
                        key="password-login"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <PasswordLogin
                          email={identifier}
                          onBack={handleBackToIdentifier}
                          onSuccess={handleSuccess}
                        />
                      </motion.div>

                    ) : loginStep === "otp" ? (
                      <motion.div
                        key="otp-login"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <OtpLogin
                          mobile={identifier}
                          onBack={handleBackToIdentifier}
                          onSuccess={handleSuccess}
                        />
                      </motion.div>

                    ) : null}
                  </AnimatePresence>

                  {/* Switch Mode - Only show when not in forgot flow */}
                  {!forgotStep && !isImageAuthLayout && (
                    <div style={{ 
                      marginTop: "20px", 
                      textAlign: "center",
                      paddingTop: "20px",
                      borderTop: "1px solid #2f343b"
                    }}>
                      <span style={{ fontSize: "14px", color: "#9ca3af" }}>
                        {authMode === "login"
                          ? "Don't have an account? "
                          : "Already have an account? "}
                        <button 
                          onClick={handleSwitchMode} 
                          style={{ 
                            background: "none", 
                            border: "none", 
                            color: "#d1d5db", 
                            fontWeight: "600", 
                            cursor: "pointer",
                            textDecoration: "underline"
                          }}
                        >
                          {authMode === "login" ? "Sign Up" : "Login"}
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
 
